<!-- Admin Details Component -->
<div class="admin-details-container" [class.fullscreen-mode]="viewMode === 'fullscreen'">

  <!-- Header Controls -->
  <mat-toolbar class="details-header">
    <span class="panel-title">Details Panel</span>

    <span class="spacer"></span>

    <!-- View Controls -->
    <div class="view-controls">
      <!-- Show Metadata Toggle -->
      <mat-checkbox
        [checked]="showMetadata"
        (change)="toggleMetadata()"
        color="primary"
        matTooltip="Show/Hide Metadata">
        Metadata
      </mat-checkbox>

      <!-- Layout Direction Toggle -->
      <button mat-icon-button
              (click)="toggleLayoutDirection()"
              [matTooltip]="'Switch to ' + (layoutDirection === 'horizontal' ? 'vertical' : 'horizontal') + ' layout'"
              class="layout-button">
        <mat-icon>{{ getLayoutIcon() }}</mat-icon>
      </button>

      <!-- Fullscreen Toggle -->
      <button mat-icon-button
              (click)="toggleFullscreen()"
              [matTooltip]="viewMode === 'fullscreen' ? 'Exit Fullscreen' : 'Enter Fullscreen'"
              class="fullscreen-button">
        <mat-icon>{{ getViewModeIcon() }}</mat-icon>
      </button>

      <!-- Close Button -->
      <button mat-icon-button
              (click)="onCloseDetails()"
              matTooltip="Close Details Panel"
              class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </mat-toolbar>

  <!-- Show Mains Checkbox (only in fullscreen mode) -->
  <mat-toolbar *ngIf="viewMode === 'fullscreen'" color="transparent" class="fullscreen-controls">
    <mat-checkbox
      [checked]="showMains"
      (change)="onShowMainsChange()"
      color="primary">
      Show Main Area
    </mat-checkbox>
  </mat-toolbar>

  <mat-divider></mat-divider>

  <!-- Content Area -->
  <div class="details-content" *ngIf="selectedFile; else noFileSelected">

    <!-- Loading Indicator -->
    <div *ngIf="isLoadingImages" class="images-loading-overlay">
      <div class="loading-content">
        <mat-progress-spinner diameter="50" mode="indeterminate" color="primary"></mat-progress-spinner>
        <p>Loading images...</p>
      </div>
    </div>

    <!-- Zoom and Navigation Controls -->
    <mat-toolbar color="transparent" class="image-controls">
      <!-- Navigation Controls -->
      <div class="navigation-controls">
        <button mat-icon-button
                (click)="navigateFile('previous')"
                matTooltip="Previous Image (←)"
                class="nav-button">
          <mat-icon>keyboard_arrow_left</mat-icon>
        </button>

        <span class="file-info">
          {{ selectedFile.page_id }}
        </span>

        <button mat-icon-button
                (click)="navigateFile('next')"
                matTooltip="Next Image (→)"
                class="nav-button">
          <mat-icon>keyboard_arrow_right</mat-icon>
        </button>
      </div>

      <span class="spacer"></span>

      <!-- Zoom Controls -->
      <div class="zoom-controls">
        <button mat-icon-button
                [disabled]="!canZoomOut()"
                (click)="zoomOut()"
                matTooltip="Zoom Out (-)"
                class="zoom-button">
          <mat-icon>zoom_out</mat-icon>
        </button>

        <span class="zoom-level">{{ getZoomPercentage() }}</span>

        <button mat-icon-button
                [disabled]="!canZoomIn()"
                (click)="zoomIn()"
                matTooltip="Zoom In (+)"
                class="zoom-button">
          <mat-icon>zoom_in</mat-icon>
        </button>

        <button mat-icon-button
                (click)="fitToScreen()"
                matTooltip="Fit to Screen (0)"
                class="zoom-button">
          <mat-icon>fit_screen</mat-icon>
        </button>
      </div>
    </mat-toolbar>

    <mat-divider></mat-divider>

    <!-- Images Container -->
    <div class="images-container"
         [class.horizontal-layout]="layoutDirection === 'horizontal'"
         [class.vertical-layout]="layoutDirection === 'vertical'"
         [class.three-panel]="hasReprocessedImage()"
         [class.single-panel]="isBucketFilePreview()">

      <!-- Original Image Panel (for processed files) -->
      <div class="image-panel" *ngIf="hasOriginalImage() && !isBucketFilePreview()">
        <div class="panel-header">
          <h4>Original Image</h4>
        </div>
        <div class="image-wrapper"
             (mousedown)="startPan($event)"
             (mousemove)="onPan($event)"
             (mouseup)="stopPan()"
             (mouseleave)="stopPan()"
             [class.panning]="isPanning">
          <img [src]="selectedFile.original_page_url"
               [alt]="'Original: ' + selectedFile.page_id"
               class="detail-image"
               data-image-type="original"
               [style.transform]="getImageTransform()"
               (error)="onImageError($event)"
               (load)="onImageLoad($event)"
               draggable="false">
        </div>
      </div>

      <!-- Bucket File Preview Panel - Clean image view without header -->
      <div class="image-panel bucket-preview-panel" *ngIf="isBucketFilePreview()">
        <!-- Loading indicator while fetching image -->
        <div *ngIf="isBucketImageLoading" class="bucket-image-loading">
          <mat-progress-spinner diameter="40" mode="indeterminate" color="primary"></mat-progress-spinner>
          <p>Loading image...</p>
        </div>
        
        <!-- Image preview when loaded -->
        <div class="image-wrapper bucket-image-wrapper"
             *ngIf="!isBucketImageLoading && bucketPreviewBlobUrl"
             (mousedown)="startPan($event)"
             (mousemove)="onPan($event)"
             (mouseup)="stopPan()"
             (mouseleave)="stopPan()"
             [class.panning]="isPanning">
          <img [src]="bucketPreviewBlobUrl"
               [alt]="selectedFile.page_id"
               class="detail-image"
               data-image-type="preview"
               [style.transform]="getImageTransform()"
               (error)="onImageError($event)"
               (load)="onImageLoad($event)"
               draggable="false">
        </div>
        
        <!-- Error state when image fails to load -->
        <div *ngIf="!isBucketImageLoading && !bucketPreviewBlobUrl" class="bucket-image-error">
          <mat-icon>broken_image</mat-icon>
          <p>Failed to load image</p>
        </div>
      </div>

      <!-- Current Processed Image Panel -->
      <div class="image-panel" *ngIf="hasWatermarkRemovedImage()">
        <div class="panel-header">
          <h4>Current Result</h4>
          <span class="script-info" *ngIf="selectedFile?.selected_script">
            ({{ getScriptDisplayName(selectedFile.selected_script) }})
          </span>
        </div>
        <div class="image-wrapper"
             (mousedown)="startPan($event)"
             (mousemove)="onPan($event)"
             (mouseup)="stopPan()"
             (mouseleave)="stopPan()"
             [class.panning]="isPanning">
          <img [src]="selectedFile.watermark_removed_url"
               [alt]="'Processed: ' + selectedFile.page_id"
               class="detail-image"
               data-image-type="watermark-removed"
               [style.transform]="getImageTransform()"
               (error)="onImageError($event)"
               (load)="onImageLoad($event)"
               draggable="false">
        </div>
      </div>

      <!-- Reprocessed Image Panel (new) -->
      <div class="image-panel" *ngIf="hasReprocessedImage()">
        <div class="panel-header">
          <h4>Alternative Result</h4>
          <span class="script-info">{{ getScriptDisplayName(selectedScript) }}</span>
        </div>
        <div class="image-wrapper"
             (mousedown)="startPan($event)"
             (mousemove)="onPan($event)"
             (mouseup)="stopPan()"
             (mouseleave)="stopPan()"
             [class.panning]="isPanning">
          <img [src]="selectedFile.reprocessed_url"
               [alt]="'Reprocessed: ' + selectedFile.page_id"
               class="detail-image"
               data-image-type="reprocessed"
               [style.transform]="getImageTransform()"
               (error)="onImageError($event)"
               (load)="onImageLoad($event)"
               draggable="false">
        </div>
      </div>

      <!-- No Images Available -->
      <div *ngIf="!hasOriginalImage() && !hasWatermarkRemovedImage()" class="no-images">
        <mat-icon class="no-images-icon">image_not_supported</mat-icon>
        <p>No images available for this file</p>
      </div>
    </div>

    <!-- Validation Controls (only for processed files, not bucket preview) -->
    <div class="validation-controls" *ngIf="selectedFile && !isBucketFilePreview()">
      <mat-divider></mat-divider>
      
      <!-- Validation Section -->
      <div class="validation-section">
        <div class="validation-header">
          <mat-icon>verified</mat-icon>
          <h4>Image Validation</h4>
          <div class="validation-status" [class]="selectedFile.validation.toLowerCase()">
            <mat-icon>{{ selectedFile.validation === 'Correct' ? 'check_circle' : 'error' }}</mat-icon>
            <span>{{ selectedFile.validation || 'Not Validated' }}</span>
          </div>
        </div>
        
        <div class="validation-options">
          <mat-radio-group 
            [value]="selectedFile.validation" 
            (change)="onValidationChange($event.value)"
            class="validation-radio-group">
            
            <mat-radio-button value="Correct" color="primary" class="validation-radio correct">
              <mat-icon class="radio-icon">check_circle</mat-icon>
              <span>Correct</span>
            </mat-radio-button>
            
            <mat-radio-button value="Incorrect" color="warn" class="validation-radio incorrect">
              <mat-icon class="radio-icon">error</mat-icon>
              <span>Incorrect - Needs Alternative Processing</span>
            </mat-radio-button>
          </mat-radio-group>
        </div>
      </div>
      
      <!-- Reprocessing Section (shown when Incorrect is selected) -->
      <div class="reprocessing-section" *ngIf="selectedFile.validation === 'Incorrect'">
        
        <!-- Show reprocessed result first if available -->
        <div class="reprocessed-result" *ngIf="selectedFile.reprocessed_url && !isReprocessing">
          <div class="result-header">
            <div class="header-left">
              <mat-icon>check_circle</mat-icon>
              <span>Alternative Processing Result</span>
              <span class="script-badge">{{ getScriptDisplayName(selectedScript) }}</span>
            </div>
            <button mat-icon-button 
                    (click)="toggleFullscreen()"
                    matTooltip="View in Fullscreen"
                    class="fullscreen-btn">
              <mat-icon>fullscreen</mat-icon>
            </button>
          </div>
          
          <div class="result-preview" 
               (click)="toggleFullscreen()"
               matTooltip="Click to view in fullscreen"
               class="clickable-preview">
            <img [src]="selectedFile.reprocessed_url" 
                 alt="Alternative processing result" 
                 class="reprocessed-image"
                 (error)="onImageError($event)"
                 (load)="onImageLoad($event)">
            <div class="preview-overlay">
              <mat-icon class="fullscreen-icon">fullscreen</mat-icon>
              <span>Click to view fullscreen</span>
            </div>
          </div>
          
          <div class="result-actions">
            <button 
              mat-raised-button 
              color="primary"
              (click)="onAcceptReprocessedResult()"
              [disabled]="isAcceptingResult">
              <mat-icon>check</mat-icon>
              {{ isAcceptingResult ? 'Accepting...' : 'Accept This Result' }}
            </button>
            
            <button 
              mat-button 
              color="accent"
              (click)="onTryAnotherScript()">
              <mat-icon>refresh</mat-icon>
              Try Another Algorithm
            </button>
          </div>
        </div>
        
        <!-- Script selection section -->
        <div class="script-selection-section">
          <div class="script-header">
            <mat-icon>settings</mat-icon>
            <span>{{ selectedFile.reprocessed_url ? 'Try Different Algorithm' : 'Try Alternative Processing' }}</span>
          </div>
          
          <mat-select 
            [(value)]="selectedScript" 
            (selectionChange)="onScriptSelectionChange($event.value)"
            placeholder="Choose processing algorithm"
            class="script-selector">
            <mat-option *ngFor="let script of availableScripts" [value]="script.id">
              <div class="script-option">
                <div class="script-name">{{ script.name }}</div>
                <div class="script-description">{{ script.description }}</div>
              </div>
            </mat-option>
          </mat-select>
          
          <button 
            mat-raised-button 
            color="primary" 
            [disabled]="!selectedScript || isReprocessing"
            (click)="onReprocessWithScript()"
            class="reprocess-button">
            <mat-icon *ngIf="!isReprocessing">refresh</mat-icon>
            <mat-progress-spinner 
              *ngIf="isReprocessing" 
              diameter="20" 
              mode="indeterminate">
            </mat-progress-spinner>
            {{ isReprocessing ? 'Processing...' : (selectedFile.reprocessed_url ? 'Apply Different Algorithm' : 'Apply Alternative Processing') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Metadata Panel (hidden for bucket file preview) -->
    <div *ngIf="showMetadata && !isBucketFilePreview()" class="metadata-panel">
      <mat-divider></mat-divider>
      <div class="metadata-header">
        <h4>File Metadata</h4>
        <button mat-icon-button
                (click)="toggleMetadata()"
                matTooltip="Hide Metadata">
          <mat-icon>expand_less</mat-icon>
        </button>
      </div>
      <div class="metadata-content">
        <div *ngFor="let item of getFileMetadata() | keyvalue" class="metadata-item">
          <span class="metadata-key">{{ item.key }}:</span>
          <span class="metadata-value">{{ item.value }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- No File Selected State -->
  <ng-template #noFileSelected>
    <div class="no-file-selected">
      <mat-icon class="no-file-icon">image_search</mat-icon>
      <h3>No File Selected</h3>
      <p>Select a processed file from the main area to view details and comparison.</p>
    </div>
  </ng-template>

  <!-- Fullscreen Overlay (when in fullscreen mode) -->
  <div *ngIf="viewMode === 'fullscreen'" class="fullscreen-overlay">
    <!-- Fullscreen content will be projected here -->
    <div class="fullscreen-content">

      <!-- Loading Indicator for Fullscreen -->
      <div *ngIf="isLoadingImages" class="fullscreen-loading-overlay">
        <div class="loading-content">
          <mat-progress-spinner diameter="60" mode="indeterminate" color="accent"></mat-progress-spinner>
          <p>Loading images...</p>
        </div>
      </div>

      <!-- Fullscreen Header -->
      <div class="fullscreen-header">
        <div class="fullscreen-title">
          <span>{{ selectedFile?.page_id }}</span>
        </div>

        <div class="fullscreen-controls-bar">
          <!-- Navigation -->
          <button mat-icon-button
                  (click)="navigateFile('previous')"
                  matTooltip="Previous (←)">
            <mat-icon>keyboard_arrow_left</mat-icon>
          </button>

          <button mat-icon-button
                  (click)="navigateFile('next')"
                  matTooltip="Next (→)">
            <mat-icon>keyboard_arrow_right</mat-icon>
          </button>

          <!-- Zoom -->
          <button mat-icon-button
                  [disabled]="!canZoomOut()"
                  (click)="zoomOut()"
                  matTooltip="Zoom Out (-)">
            <mat-icon>zoom_out</mat-icon>
          </button>

          <span class="zoom-indicator">{{ getZoomPercentage() }}</span>

          <button mat-icon-button
                  [disabled]="!canZoomIn()"
                  (click)="zoomIn()"
                  matTooltip="Zoom In (+)">
            <mat-icon>zoom_in</mat-icon>
          </button>

          <button mat-icon-button
                  (click)="fitToScreen()"
                  matTooltip="Fit to Screen (0)">
            <mat-icon>fit_screen</mat-icon>
          </button>

          <!-- Layout -->
          <button mat-icon-button
                  (click)="toggleLayoutDirection()"
                  [matTooltip]="'Switch to ' + (layoutDirection === 'horizontal' ? 'vertical' : 'horizontal')">
            <mat-icon>{{ getLayoutIcon() }}</mat-icon>
          </button>

          <!-- Validation -->
          <mat-divider vertical style="margin: 0 8px;"></mat-divider>
          
          <button mat-icon-button
                  [color]="selectedFile?.validation === 'Correct' ? 'primary' : ''"
                  (click)="onValidationChange('Correct')"
                  matTooltip="Mark as Correct">
            <mat-icon>verified</mat-icon>
          </button>
          
          <button mat-icon-button
                  [color]="selectedFile?.validation === 'Incorrect' ? 'warn' : ''"
                  (click)="onValidationChange('Incorrect')"
                  matTooltip="Mark as Incorrect">
            <mat-icon>warning</mat-icon>
          </button>

          <!-- Exit -->
          <button mat-icon-button
                  (click)="exitFullscreen()"
                  matTooltip="Exit Fullscreen (Esc)">
            <mat-icon>fullscreen_exit</mat-icon>
          </button>
        </div>
      </div>

      <!-- Fullscreen Images -->
      <div class="fullscreen-images"
           [class.horizontal-layout]="layoutDirection === 'horizontal'"
           [class.vertical-layout]="layoutDirection === 'vertical'"
           [class.three-panel]="selectedFile?.reprocessed_url"
           [class.single-panel]="isBucketFilePreview()">

        <!-- Bucket File Preview (Clean single panel view) -->
        <div class="fullscreen-panel bucket-fullscreen-panel" *ngIf="isBucketFilePreview()">
          <!-- Loading state -->
          <div *ngIf="isBucketImageLoading" class="fullscreen-loading">
            <mat-progress-spinner diameter="50" mode="indeterminate" color="accent"></mat-progress-spinner>
            <p>Loading image...</p>
          </div>
          
          <!-- Image when loaded -->
          <div class="fullscreen-image-wrapper"
               *ngIf="!isBucketImageLoading && bucketPreviewBlobUrl"
               (mousedown)="startPan($event)"
               (mousemove)="onPan($event)"
               (mouseup)="stopPan()"
               (mouseleave)="stopPan()"
               [class.panning]="isPanning">
            <img [src]="bucketPreviewBlobUrl"
                 [alt]="selectedFile?.page_id"
                 class="fullscreen-image"
                 data-image-type="preview"
                 [style.transform]="getImageTransform()"
                 (error)="onImageError($event)"
                 (load)="onImageLoad($event)"
                 draggable="false">
          </div>
        </div>

        <!-- Original Image (only for non-bucket files) -->
        <div class="fullscreen-panel" *ngIf="hasOriginalImage() && !isBucketFilePreview()">
          <div class="fullscreen-panel-title">Original Image</div>
          <div class="fullscreen-image-wrapper"
               (mousedown)="startPan($event)"
               (mousemove)="onPan($event)"
               (mouseup)="stopPan()"
               (mouseleave)="stopPan()"
               [class.panning]="isPanning">
            <img [src]="selectedFile?.original_page_url"
                 [alt]="'Original: ' + selectedFile?.page_id"
                 class="fullscreen-image"
                 data-image-type="original"
                 [style.transform]="getImageTransform()"
                 (error)="onImageError($event)"
                 (load)="onImageLoad($event)"
                 draggable="false">
          </div>
        </div>

        <!-- Current Result Image -->
        <div class="fullscreen-panel" *ngIf="hasWatermarkRemovedImage()">
          <div class="fullscreen-panel-title">
            {{ selectedFile?.reprocessed_url ? 'Current Result' : 'Watermark Removed' }}
          </div>
          <div class="fullscreen-image-wrapper"
               (mousedown)="startPan($event)"
               (mousemove)="onPan($event)"
               (mouseup)="stopPan()"
               (mouseleave)="stopPan()"
               [class.panning]="isPanning">
            <img [src]="selectedFile?.watermark_removed_url"
                 [alt]="'Processed: ' + selectedFile?.page_id"
                 class="fullscreen-image"
                 data-image-type="watermark-removed"
                 [style.transform]="getImageTransform()"
                 (error)="onImageError($event)"
                 (load)="onImageLoad($event)"
                 draggable="false">
          </div>
        </div>

        <!-- Alternative Processing Result -->
        <div class="fullscreen-panel" *ngIf="selectedFile?.reprocessed_url">
          <div class="fullscreen-panel-title">Alternative Result</div>
          <div class="fullscreen-image-wrapper"
               (mousedown)="startPan($event)"
               (mousemove)="onPan($event)"
               (mouseup)="stopPan()"
               (mouseleave)="stopPan()"
               [class.panning]="isPanning">
            <img [src]="selectedFile?.reprocessed_url"
                 [alt]="'Alternative: ' + selectedFile?.page_id"
                 class="fullscreen-image"
                 data-image-type="reprocessed"
                 [style.transform]="getImageTransform()"
                 (error)="onImageError($event)"
                 (load)="onImageLoad($event)"
                 draggable="false">
          </div>
        </div>
      </div>

      <!-- Fullscreen Validation Controls (hidden for bucket file preview) -->
      <div class="fullscreen-validation-controls" *ngIf="selectedFile && !isBucketFilePreview()">
        <div class="fullscreen-validation-panel">
          <div class="fullscreen-validation-header">
            <div class="validation-info">
              <mat-icon>verified</mat-icon>
              <span class="validation-title">Image Validation</span>
              <div class="validation-status" [class]="selectedFile.validation.toLowerCase()">
                <mat-icon>{{ selectedFile.validation === 'Correct' ? 'check_circle' : 'error' }}</mat-icon>
                <span>{{ selectedFile.validation || 'Not Validated' }}</span>
              </div>
            </div>
            
            <div class="file-info">
              <span>{{ selectedFile.page_id }} • {{ selectedFile.book_name }}</span>
            </div>
          </div>
          
                     <div class="fullscreen-validation-options">
             <mat-radio-group 
               [value]="selectedFile.validation" 
               (change)="onValidationChange($event.value)"
               class="fullscreen-radio-group">
               
               <mat-radio-button value="Correct" color="primary" class="fullscreen-validation-radio correct">
                 <mat-icon class="radio-icon">check_circle</mat-icon>
                 <span>Correct</span>
               </mat-radio-button>
               
               <mat-radio-button value="Incorrect" color="warn" class="fullscreen-validation-radio incorrect">
                 <mat-icon class="radio-icon">error</mat-icon>
                 <span>Incorrect - Needs Alternative Processing</span>
               </mat-radio-button>
             </mat-radio-group>
             
             <!-- Quick action buttons -->
             <div class="quick-actions">
               <button mat-button 
                       color="primary"
                       (click)="navigateFile('previous')"
                       [disabled]="!canNavigatePrevious()">
                 <mat-icon>keyboard_arrow_left</mat-icon>
                 Previous
               </button>
               
               <button mat-button 
                       color="primary"
                       (click)="navigateFile('next')"
                       [disabled]="!canNavigateNext()">
                 Next
                 <mat-icon>keyboard_arrow_right</mat-icon>
               </button>
             </div>
           </div>
           
           <!-- Fullscreen Reprocessing Section (shown when Incorrect is selected) -->
           <div class="fullscreen-reprocessing-section" *ngIf="selectedFile.validation === 'Incorrect'">
             
             <!-- Show reprocessed result if available -->
             <div class="fullscreen-reprocessed-result" *ngIf="selectedFile.reprocessed_url && !isReprocessing">
               <div class="fullscreen-result-header">
                 <div class="result-info">
                   <mat-icon>check_circle</mat-icon>
                   <span>Alternative Result Available</span>
                   <span class="script-badge">{{ getScriptDisplayName(selectedScript) }}</span>
                 </div>
                 <button mat-button
                         color="primary" 
                         (click)="onAcceptReprocessedResult()"
                         [disabled]="isAcceptingResult">
                   <mat-icon>check</mat-icon>
                   {{ isAcceptingResult ? 'Accepting...' : 'Accept' }}
                 </button>
               </div>
             </div>
             
             <!-- Script selection -->
             <div class="fullscreen-script-selection">
               <div class="script-header">
                 <mat-icon>settings</mat-icon>
                 <span>{{ selectedFile.reprocessed_url ? 'Try Different Algorithm' : 'Select Processing Algorithm' }}</span>
               </div>
               
               <div class="script-controls">
                 <mat-select 
                   [(value)]="selectedScript" 
                   (selectionChange)="onScriptSelectionChange($event.value)"
                   placeholder="Choose algorithm"
                   class="fullscreen-script-selector">
                   <mat-option *ngFor="let script of availableScripts" [value]="script.id">
                     <div class="script-option">
                       <div class="script-name">{{ script.name }}</div>
                       <div class="script-description">{{ script.description }}</div>
                     </div>
                   </mat-option>
                 </mat-select>
                 
                 <button 
                   mat-raised-button 
                   color="accent" 
                   [disabled]="!selectedScript || isReprocessing"
                   (click)="onReprocessWithScript()"
                   class="fullscreen-reprocess-button">
                   <mat-icon *ngIf="!isReprocessing">refresh</mat-icon>
                   <mat-progress-spinner 
                     *ngIf="isReprocessing" 
                     diameter="18" 
                     mode="indeterminate"
                     color="primary">
                   </mat-progress-spinner>
                   {{ isReprocessing ? 'Processing...' : 'Apply Algorithm' }}
                 </button>
               </div>
             </div>
           </div>
        </div>
      </div>

      <!-- Fullscreen Help -->
      <div class="fullscreen-help">
        <p>Use arrow keys to navigate • +/- to zoom • F to toggle fullscreen • Esc to exit • Space to validate</p>
      </div>
    </div>
  </div>
</div>
